import React, { useEffect, useState, ChangeEvent } from "react";
import api from "../utils/api";
import styles from "./OrganizationsPage.module.scss";
import { Eye, PencilSimple, Trash } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import DataTable from "../components/DataTable/DataTable";
import LoadingOverlay from "../components/LoadingOverlay/LoadingOverlay";
import logoPlaceholder from "../assets/org-logo-placeholder.png";

// Define interfaces for type safety
interface Organization {
  id: string;
  name: string;
  user_count: number;
  admin_name: string | null;
  created_at: string;
}

interface LogoResponse {
  file_path: string | null;
  signed_url: string | null;
}

interface AppContext {
  setEditOrganizationName: (name: string) => void;
}

interface AxiosResponse<T> {
  data: T;
}

interface AxiosError extends Error {
  message: string;
}

const ORGS_PER_PAGE = 10;

export default function OrganizationsPage() {
  const [allOrgs, setAllOrgs] = useState<Organization[]>([]);
  const [filteredOrgs, setFilteredOrgs] = useState<Organization[]>([]);
  const [logoImages, setLogoImages] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [confirmDeleteOrg, setConfirmDeleteOrg] = useState<Organization | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const navigate = useNavigate();
  const { setEditOrganizationName } = useAppContext() as AppContext;

  const fetchOrgs = async () => {
    try {
      setError("");
      const res = await api.get<AxiosResponse<Organization[]>>("/core/organization/list");
      const orgs = Array.isArray(res.data) ? res.data : [];
      console.log("[fetchOrgs] Response:", res.data);
      setAllOrgs(orgs);
      setFilteredOrgs(orgs);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to load organizations";
      console.error("[fetchOrgs] Error:", errorMessage);
      setError(errorMessage);
      setAllOrgs([]);
      setFilteredOrgs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogos = async (orgs: Organization[]) => {
    const newImages: { [key: string]: string } = {};

    await Promise.all(
      orgs.map(async (org) => {
        if (logoImages[org.id]) {
          newImages[org.id] = logoImages[org.id];
          return;
        }

        try {
          const res = await api.post<AxiosResponse<LogoResponse>>(
            '/core/organization/get-logo',
            { organization_id: org.id }
          );
          console.log(`[fetchLogos] Logo response for org ${org.id}:`, res.data);
          if (res.data.signed_url) {
            newImages[org.id] = res.data.signed_url;
          } else {
            newImages[org.id] = logoPlaceholder;
          }
        } catch (err: unknown) {
          console.error(`[fetchLogos] Failed to fetch logo for org ${org.id}:`, err);
          newImages[org.id] = logoPlaceholder;
        }
      })
    );

    setLogoImages((prev) => ({ ...prev, ...newImages }));
  };

  useEffect(() => {
    fetchOrgs();
  }, []);

  useEffect(() => {
    const query = searchQuery.toLowerCase();
    const filtered = allOrgs.filter((org) => org.name?.toLowerCase().includes(query));
    setFilteredOrgs(filtered);
    setCurrentPage(1);
  }, [searchQuery, allOrgs]);

  useEffect(() => {
    const paginatedOrgs = filteredOrgs.slice(
      (currentPage - 1) * ORGS_PER_PAGE,
      currentPage * ORGS_PER_PAGE
    );
    fetchLogos(paginatedOrgs);
  }, [filteredOrgs, currentPage]);

  const handleDeleteOrg = (org: Organization) => {
    setConfirmDeleteOrg(org);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteOrg) return;
    try {
      await api.post("/core/organization/delete", {
        organizationId: confirmDeleteOrg.id,
      });
      await fetchOrgs();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Delete failed";
      console.error("[confirmDelete] Error:", errorMessage);
      setError(errorMessage);
    } finally {
      setShowDeleteModal(false);
      setConfirmDeleteOrg(null);
    }
  };

  const columns = [
    {
      label: "Logo",
      accessor: "logo",
      render: (row: Organization) => (
        <img
          src={logoImages[row.id] || logoPlaceholder}
          alt={`${row.name} logo`}
          className={styles.logoImage}
        />
      ),
    },
    {
      label: "Name",
      accessor: "name",
    },
    {
      label: "User Count",
      accessor: "user_count",
      render: (row: Organization) => row.user_count || 0,
    },
    {
      label: "Admin User",
      accessor: "admin_name",
      render: (row: Organization) => row.admin_name || "None",
    },
    {
      label: "Created At",
      accessor: "created_at",
      render: (row: Organization) =>
        row.created_at ? new Date(row.created_at).toLocaleString() : "—",
    },
  ];

  const actions = (org: Organization) => (
    <div className={styles.actions}>
      <button
        onClick={() => navigate(`/organizations/view/${org.id}`)}
        title="View"
      >
        <Eye size={20} />
      </button>
      <button
        onClick={() => {
          setEditOrganizationName(org.name);
          navigate(`/organizations/edit/${org.id}`);
        }}
        title="Edit"
      >
        <PencilSimple size={20} />
      </button>
      <button
        onClick={() => handleDeleteOrg(org)}
        title="Delete"
        className={styles.deleteBtn}
      >
        <Trash size={20} />
      </button>
    </div>
  );

  const paginatedOrgs = filteredOrgs.slice(
    (currentPage - 1) * ORGS_PER_PAGE,
    currentPage * ORGS_PER_PAGE
  );

  const totalPages = Math.ceil(filteredOrgs.length / ORGS_PER_PAGE);

  return (
    <div className={styles.container}>
      {error && <div className={styles.error}>{error}</div>}
      <div className={styles.header}>
        <button onClick={() => navigate("/organizations/new")}>
          + Add Organization
        </button>
        <input
          type="text"
          placeholder="Search organizations..."
          value={searchQuery}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      {loading && <LoadingOverlay />}

      {!loading && !error && (
        <>
          <DataTable
            columns={columns}
            data={paginatedOrgs}
            actions={actions}
            emptyMessage="No organizations found."
          />

          {totalPages > 1 && (
            <div className={styles.pagination}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  onClick={() => setCurrentPage(num)}
                  className={num === currentPage ? styles.activePage : ""}
                >
                  {num}
                </button>
              ))}
            </div>
          )}

          {showDeleteModal && confirmDeleteOrg && (
            <div className={styles.modalBackdrop}>
              <div className={styles.modal}>
                <p>
                  Are you sure you want to delete{" "}
                  <strong>{confirmDeleteOrg.name}</strong>?
                </p>
                <div className={styles.modalActions}>
                  <button onClick={confirmDelete} className={styles.confirmBtn}>
                    Yes, Delete
                  </button>
                  <button onClick={() => setShowDeleteModal(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
